import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronLeft, ChevronRight, FileStack, Loader2, Plus, RefreshCw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { canWrite, recordsError } from "../lib/records";
import { TemplateStatus } from "../components/records/TemplateBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function NewTemplateDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setName(""); setType(""); setError("");
    api.get("/records/types").then(({ data }) => setTypes(data.items.filter((t) => t.workflow !== "received"))).catch(() => setTypes([]));
  }, [open]);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/records/templates", { name: name.trim(), type_code: type,
        blocks: [{ kind: "title", text: name.trim() }, { kind: "article", text: "Objet" }, { kind: "paragraph", text: "" }, { kind: "signatures" }] });
      toast.success("Modèle créé : son brouillon est ouvert.");
      navigate(`/documents/modeles/${data.id}`);
    } catch (e) {
      setError(recordsError(e, "Création impossible. Réessayez."));
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveau modèle</DialogTitle>
          <DialogDescription>Un modèle produit un document de Jamora (contrat, convention…) qui suivra ensuite le circuit habituel.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="template-name">Nom</Label>
            <Input id="template-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} className="h-10"
              placeholder="Ex. Contrat de service" data-testid="template-name" /></div>
          <div className="space-y-1.5"><Label htmlFor="template-type">Type de document produit</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="template-type" className="h-10" data-testid="template-type"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Fermer</Button>
          <Button onClick={submit} disabled={busy || name.trim().length < 2 || !type} data-testid="template-create">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Créer le modèle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecordTemplates() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    setError("");
    try {
      const { data: body } = await api.get("/records/templates", { params: { include_inactive: showInactive } });
      setData(body);
    } catch (e) {
      setError(recordsError(e, "Impossible de charger les modèles."));
    }
  }, [showInactive]);
  useEffect(() => { load(); }, [load]);
  const canManage = !!data?.can_manage;
  const canGenerate = canWrite(user, "records:create");

  return (
    <div className="space-y-4" data-testid="templates-page">
      <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft size={14} />Documents et contrats</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Modèles de documents</h1>
          <p className="text-sm text-slate-500">Des contrats prêts à remplir : choisissez une pharmacie, le PDF est produit et entre dans le registre en brouillon.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canGenerate && (
            <Button variant={canManage ? "outline" : "default"} asChild data-testid="templates-generate">
              <Link to="/documents/generer"><Wand2 size={16} className="mr-1.5" />Générer un document</Link>
            </Button>
          )}
          {canManage && <Button onClick={() => setCreating(true)} data-testid="templates-new"><Plus size={16} className="mr-1.5" />Nouveau modèle</Button>}
        </div>
      </div>

      {canManage && (
        <label className="flex items-center justify-end gap-2 text-sm text-slate-600">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} />Afficher les modèles désactivés
        </label>
      )}
      {error && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {!data && !error && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {data && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="templates-empty">
          <FileStack className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">Aucun modèle {canManage ? "pour le moment" : "publié"}</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {canManage ? "Écrivez un contrat type une fois, avec ses variables ({pharmacie.nom}, {abonnement.prix}…) ; il se remplit tout seul ensuite."
              : "Un responsable du registre doit d'abord publier un modèle."}
          </p>
        </Card>
      )}
      {data && data.items.length > 0 && (
        <Card className="divide-y divide-slate-100" data-testid="templates-list">
          {data.items.map((t) => (
            <Link key={t.id} to={`/documents/modeles/${t.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`template-${t.id}`}>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 break-words">{t.name}</span>
                  <TemplateStatus template={t} />
                </div>
                {t.description && <p className="text-xs text-slate-500 truncate">{t.description}</p>}
              </div>
              <ChevronRight size={16} className="shrink-0 text-slate-300" />
            </Link>
          ))}
        </Card>
      )}
      <NewTemplateDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
